import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const SignedAgreements = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      const res = await api.get('/agreements/list.php');
      setAgreements(res.data.agreements || []);
    } catch (err) {
      console.error('Failed to fetch agreements', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyAgreement = async (agreement) => {
    setSelectedAgreement(agreement);
    setVerifyResult({ checking: true });
    setModalVisible(true);

    try {
      const res = await api.get(`/agreements/verify.php?id=${agreement.id}`);
      setVerifyResult({
        checking: false,
        isValid: res.data.is_valid,
        message: res.data.message,
        signedAt: res.data.signed_at,
        signerName: res.data.signer_name,
        signerEmail: res.data.signer_email,
        storedHash: agreement.signed_document_hash,
      });
    } catch (err) {
      setVerifyResult({
        checking: false,
        isValid: false,
        message: err.response?.data?.error || 'Verification failed',
        storedHash: agreement.signed_document_hash,
      });
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedAgreement(null);
    setVerifyResult(null);
  };

  const downloadAgreement = async (agreementId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You are not logged in.');
      return;
    }

    try {
      const response = await fetch(`/backend/api/agreements/download.php?id=${agreementId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agreement_${agreementId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert(error.message);
    }
  };

  if (loading) return <div className="loading">Loading signed agreements...</div>;

  if (agreements.length === 0) {
    return <div className="empty-state">No signed agreements yet.</div>;
  }

  return (
    <div className="signed-agreements-container">
      <h2>Signed Order Agreements</h2>
      <table className="agreements-table">
        <thead>
          <tr>
            <th>Order #</th>
            <th>Signed By</th>
            <th>Email</th>
            <th>Signed On</th>
            <th>Status</th>
            <th>Verify</th>
            <th>Download</th>
          </tr>
        </thead>
        <tbody>
          {agreements.map(ag => (
            <tr key={ag.id}>
              <td>{ag.order_id}</td>
              <td>{ag.signer_name}</td>
              <td>{ag.signer_email}</td>
              <td>{new Date(ag.signed_at).toLocaleDateString()}</td>
              <td className={`status-${ag.status}`}>{ag.status}</td>
              <td>
                <button
                  onClick={() => verifyAgreement(ag)}
                  className="verify-btn"
                >
                  Verify Integrity
                </button>
              </td>
              <td>
                <button
                  onClick={() => downloadAgreement(ag.id)}
                  className="download-btn"
                >
                  📄 Download PDF
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Verification Modal */}
      {modalVisible && selectedAgreement && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Document Integrity Verification</h3>
            <p><strong>Order Agreement ID:</strong> {selectedAgreement.id}</p>
            <p><strong>Signed by:</strong> {selectedAgreement.signer_name} ({selectedAgreement.signer_email})</p>
            <p><strong>Signed on:</strong> {new Date(selectedAgreement.signed_at).toLocaleString()}</p>

            {verifyResult?.checking ? (
              <p>Verifying cryptographic hash...</p>
            ) : (
              <>
                <div className={`verification-badge ${verifyResult?.isValid ? 'valid' : 'invalid'}`}>
                  {verifyResult?.isValid ? '✅ VERIFIED' : '❌ TAMPERED'}
                </div>
                <p><strong>Verification message:</strong> {verifyResult?.message}</p>

                <div className="hash-details">
                  <p><strong>Stored SHA‑256 Hash:</strong></p>
                  <code className="hash-value">{selectedAgreement.signed_document_hash}</code>
                  <p className="hash-explanation">
                    This hash was computed from the original signed PDF at the time of signing.
                    {verifyResult?.isValid
                      ? ' The current file matches this hash – no alterations detected.'
                      : ' The current file does NOT match this hash – the document has been changed since signing.'}
                  </p>
                </div>

                {!verifyResult?.isValid && (
                  <p className="warning">
                    ⚠️ This document may have been modified after signing. The signature is no longer valid.
                  </p>
                )}
              </>
            )}

            <div className="modal-buttons">
              <button onClick={closeModal} className="close-modal">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignedAgreements;